var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useCallback, useState } from 'react';
import { Trans } from 'react-i18next';
import { message } from 'antd';
import i18n from '../../../i18n';
import { useStore } from '../../../store/rootStore';
import { observer } from 'mobx-react';
import { getScenarioInfo } from '../helpers';
import ScenarioDraftWizard from './components/ScenarioDraftWizard/ScenarioDraftWizard';
import ScenarioCreateView from './components/ScenarioCreateView';
import ScenarioDetailView from './components/ScenarioDetailView';
const ScenariosForm = React.forwardRef(({ entity, onInput, onCopyRevision, initialValues, setDraftSaveTrigger, isSystemScenario }, ref) => {
    const { scenarios } = useStore();
    const [loadingActions, setLoadingActions] = useState(new Set());
    const [editingDraft, setEditingDraft] = useState(null);
    const withLoading = (key, fn) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b;
        setLoadingActions((prev) => new Set(prev).add(key));
        try {
            return yield fn();
        }
        catch (error) {
            const errorMessage = ((_b = (_a = error === null || error === void 0 ? void 0 : error.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.message) ||
                (error === null || error === void 0 ? void 0 : error.message) ||
                i18n.t('unexpectedError');
            message.error(errorMessage);
            throw error;
        }
        finally {
            setLoadingActions((prev) => {
                const next = new Set(prev);
                next.delete(key);
                return next;
            });
        }
    });
    const handleWizardDataChange = useCallback((wizardData) => {
        var _a;
        if (onInput) {
            const canBeSaved = !!(wizardData.name &&
                wizardData.triggerEvents &&
                wizardData.triggerEvents.length > 0 &&
                wizardData.actions &&
                wizardData.actions.length > 0);
            onInput({
                name: wizardData.name,
                description: wizardData.description,
                triggerEvent: (_a = wizardData.triggerEvents) === null || _a === void 0 ? void 0 : _a[0], // Take first trigger
            }, canBeSaved);
        }
    }, []);
    const handleSaveDraft = (draft, operations) => __awaiter(void 0, void 0, void 0, function* () {
        if (!entity)
            return;
        if (operations.length === 0) {
            message.info(React.createElement(Trans, { i18nKey: "noChangesToSave", defaults: "No changes to save." }));
            setEditingDraft(null);
            return;
        }
        try {
            const result = yield withLoading(`save-draft-${draft._id}`, () => scenarios.editDraft(entity._id, operations));
            if (result) {
                setEditingDraft(null);
                message.success(React.createElement(Trans, { i18nKey: "draftSavedSuccess", defaults: "Draft saved successfully" }));
            }
        }
        catch (error) {
            console.error("[ScenariosForm] Error saving draft", error);
            throw error;
        }
    });
    // Edit Draft View
    if (editingDraft) {
        return (React.createElement(ScenarioDraftWizard, { draft: editingDraft, onSave: handleSaveDraft, onCancel: () => setEditingDraft(null), loading: loadingActions.has(`save-draft-${editingDraft._id}`), scenariosStore: scenarios, onDataChange: handleWizardDataChange, setSaveTrigger: setDraftSaveTrigger }));
    }
    const handleCopy = (revision) => {
        if (!revision || !onCopyRevision || !entity)
            return;
        onCopyRevision(revision, entity);
    };
    const handleNewDraft = (revision) => __awaiter(void 0, void 0, void 0, function* () {
        if (!revision || !entity)
            return;
        message.info(React.createElement(Trans, { i18nKey: "creatingDraftFromRevision", values: { version: revision.version }, defaults: "Creating new draft from revision {{version}}" }));
        const updatedScenario = yield withLoading(`new-draft-${revision.version}`, () => scenarios.createDraft(entity._id, { fromVersion: revision.version }));
        if (updatedScenario) {
            const oldDraftIds = new Set((entity.drafts || []).map((d) => d._id));
            const newDraft = (updatedScenario.drafts || []).find((d) => !oldDraftIds.has(d._id));
            if (newDraft) {
                setEditingDraft(newDraft);
                message.success(React.createElement(Trans, { i18nKey: "draftCreatedFromRevision", values: { version: revision.version }, defaults: "New draft created from revision {{version}}" }));
            }
        }
    });
    const handleActivate = (revision) => __awaiter(void 0, void 0, void 0, function* () {
        if (!revision || !entity)
            return;
        const result = yield withLoading(`activate-${revision.version}`, () => scenarios.activateRevision(entity._id, revision.version));
        if (result) {
            message.success(React.createElement(Trans, { i18nKey: "revisionActivated", values: { version: revision.version }, defaults: "Revision {{version}} activated" }));
        }
    });
    const handleInactivate = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!entity)
            return;
        const result = yield withLoading('inactivate', () => scenarios.inactivateRevision(entity._id));
        if (result) {
            message.success(React.createElement(Trans, { i18nKey: "scenarioInactivated", defaults: "Scenario inactivated" }));
        }
    });
    const handleDeleteRevision = (revision) => __awaiter(void 0, void 0, void 0, function* () {
        if (!revision || !entity)
            return;
        const result = yield withLoading(`delete-revision-${revision.version}`, () => scenarios.deleteRevision(entity._id, revision.version));
        if (result) {
            message.success(React.createElement(Trans, { i18nKey: "revisionDeleted", values: { version: revision.version }, defaults: "Revision {{version}} deleted" }));
        }
    });
    const handlePromoteDraft = (draft, comment) => __awaiter(void 0, void 0, void 0, function* () {
        if (!entity)
            return;
        const result = yield withLoading(`promote-${draft._id}`, () => scenarios.promoteToRevision(entity._id, comment));
        if (result) {
            message.success(React.createElement(Trans, { i18nKey: "draftPromoted", defaults: "Draft promoted to new revision" }));
        }
    });
    const handleDeleteDraft = (draft) => __awaiter(void 0, void 0, void 0, function* () {
        if (!entity)
            return;
        const result = yield withLoading(`delete-draft-${draft._id}`, () => scenarios.discardDraft(entity._id));
        if (result) {
            message.success(React.createElement(Trans, { i18nKey: "draftDiscarded", defaults: "Draft discarded" }));
        }
    });
    // Detail View
    if (entity) {
        const { name, description } = getScenarioInfo(entity);
        const revisions = entity.inactiveRevisions || [];
        const drafts = entity.drafts || [];
        return (React.createElement(ScenarioDetailView, { name: name, description: description, revisions: revisions, drafts: drafts, activeRevision: entity.activeRevision, loadingActions: loadingActions, onCopyRevision: (revision) => handleCopy(revision), onNewDraft: handleNewDraft, onActivateRevision: handleActivate, onInactivateRevision: handleInactivate, onDeleteRevision: handleDeleteRevision, onPromoteDraft: handlePromoteDraft, onDeleteDraft: handleDeleteDraft, onEditDraft: setEditingDraft, isSystemScenario: isSystemScenario }));
    }
    // Create View
    return (React.createElement(ScenarioCreateView, { formRef: ref, onValuesChange: (_, allValues) => onInput(allValues, true), initialValues: initialValues, events: scenarios.events }));
});
export default observer(ScenariosForm);
